/**
 * ProducersMap Presentational Component
 * For more informations about this kind of component, read README
 *
 * References:
 *  - https://stackoverflow.com/questions/18883601/function-to-calculate-distance-between-two-coordinates
 */

import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { useHistory } from 'react-router-dom';
import { FaStreetView } from 'react-icons/fa';

import {
  MapContainer, TileLayer, Marker, Popup,
} from 'react-leaflet';
import Leaflet from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';

import { Image } from '../../../../components';
import ProducerPopup from '../ProducerPopup';
import UserPopup from '../UserPopup';

import 'react-leaflet-markercluster/dist/styles.min.css';
import styles from './ProducersMap.module.css';

/**
 * useProducersMap UI logic hook.
 * Enable eventsHandlres to control UI behaviour and localStates to represent UI states.
 * @param {Commands} commands
 * @returns local states and events handles
 */
function useProducersMap(commands) {
  const history = useHistory();

  const handleMapCreated = (map) => commands.initMap(map);
  const handleOpenProducerProfile = (nickname) => history.push(`/${nickname}`);
  const handleClickInProducer = (markerId) => { commands.openPopup(markerId); };
  // const handleShareProducer = () => {};

  return {
    localStates: {
    },
    eventsHandlers: {
      handleMapCreated,
      handleOpenProducerProfile,
      handleClickInProducer,
    },
  };
}

/**
 * ProducersMap purely presentational component
 * Get user events and pass it to UI hook
 * @returns JSX presentational component
 */
export default function ProducersMap(props) {
  const {
    commands,
    sharedStates,
  } = props;

  const { eventsHandlers } = useProducersMap(commands);

  return (
    <MapContainer
      zoom={8}
      center={[-15.658180261054607, -47.845692125673125]}
      scrollWheelZoom
      zoomControl={false}
      className={styles['leaflet-container']}
      whenCreated={eventsHandlers.handleMapCreated}
      preferCanvas
    >
      <TileLayer
        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {sharedStates.userLocation === null ? null : (
        <Marker
          markerId="userMarker"
          zIndexOffset={10000}
          icon={
            Leaflet.divIcon({
              className: styles['leaflet-user-icon-container'],
              popupAnchor: [0, -32],
              html: ReactDOMServer.renderToString(
                <div className={styles['leaflet-user-icon']}>
                  <FaStreetView size={32} color="#cf5648" />
                </div>,
              ),
            })
          }
          position={sharedStates.userLocation}
        >
          <Popup
            autoPan
            autoPanPaddingTopLeft={[10, 60]}
            autoPanPaddingBottomRight={[10, 150]}
            closeButton
          >
            <UserPopup
              commands={commands}
              sharedStates={sharedStates}
            />
          </Popup>
        </Marker>
      )}

      <MarkerClusterGroup removeOutsideVisibleBounds={false}>
        { sharedStates.producers?.filter((producer) => {
          const producerHasImages = producer.imagesPaths?.length;
          // const producerHasOffers = sharedStates.offers?.find((offer) => offer.producerId === producer.id);

          return producerHasImages;
        })
          .map((producer) => producer.addresses.map((address) => {
            const producerIcon = Leaflet.divIcon({
              iconSize: [60, 71],
              className: styles['leaflet-producer-icon-container'],
              popupAnchor: [0, -70],
              html: ReactDOMServer.renderToString(
                <div
                  className={styles['leaflet-icon']}
                >
                  <div className={styles['leaflet-icon-img']}>
                    <Image
                      src={producer.imagesPaths.length ? producer.imagesPaths[producer.imagesPaths.length - 1] : ''}
                      alt={producer.name}
                      rounded
                      spinner={false}
                    />
                  </div>
                  <div className={styles['leaflet-icon-triangle']} />
                </div>,
              ),
            });

            return (
              <Marker
                key={address.id}
                markerId={address.id}
                addressId={address.id}
                address={address}
                producerId={producer.id}
                icon={producerIcon}
                position={[address.lat, address.lng]}
                eventHandlers={{
                  click: () => eventsHandlers.handleClickInProducer(address.id),
                }}
              >
                <Popup
                  autoPan
                  autoPanPaddingTopLeft={[10, 60]}
                  autoPanPaddingBottomRight={[10, 150]}
                  keepInView
                  className={styles['producer-popup']}
                  producerId={producer.id}
                >
                  <ProducerPopup
                    producer={producer}
                    address={address}
                    offers={sharedStates.offers?.filter((offer) => offer.producerId === producer.id)}
                  />
                </Popup>
              </Marker>
            );
          }))}
      </MarkerClusterGroup>
    </MapContainer>
  );
}
